// ---------------------------------------------------------
// Datei: uploadDeadline.ts
// Zweck: Anhaltbare Frist (Deadline) pro Queue-Item, gekoppelt an dessen AbortSignal.
// Kontext: queueService.processQueue startet die 30s-Frist eines Items, bevor der eigentliche
//          Upload läuft. Wartet ein Upload in api/drive.ts auf die Ordner-Anlage eines anderen
//          Uploads (ADO #1506, withFolderCreationGate), darf diese Wartezeit nicht vom
//          Upload-Budget abgehen — sonst laufen zwei funktionierende Uploads (je 20s) in den
//          Timeout. Die Frist wird daher während der Wartephase pausiert und beim tatsächlichen
//          Start mit vollem Budget neu gestartet.
// Stand: 2026-10-20
// ---------------------------------------------------------

export interface UploadDeadline {
  /** Hält die Frist an (z.B. während der Wartephase auf die Ordner-Anlage). */
  pause: () => void;
  /** Startet die Frist mit vollem Budget neu (Upload beginnt tatsächlich). */
  resume: () => void;
}

const deadlines = new WeakMap<AbortSignal, UploadDeadline>();

/**
 * Erzeugt eine Frist, die bei Ablauf `onExpire` aufruft, und koppelt sie an `signal`.
 * Läuft ab sofort; `cancel` räumt den Timer auf.
 */
export function createUploadDeadline(
  signal: AbortSignal,
  ms: number,
  onExpire: () => void,
): { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(onExpire, ms);
  };
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
    deadlines.delete(signal);
  };
  deadlines.set(signal, { pause: () => clearTimeout(timer), resume: arm });
  arm();
  return { cancel };
}

/** Liefert die Frist zu einem Signal (undefined, wenn keine existiert, z.B. im Wizard). */
export const getUploadDeadline = (signal?: AbortSignal): UploadDeadline | undefined =>
  signal ? deadlines.get(signal) : undefined;
